// share_link.js — the '#p=…' program link this page was opened with.
//
// The token is read ONCE (at boot, and again only when a new '#p=' arrives
// through hashchange) and the fragment is stripped from the URL at once, before
// any sheet pushes a history entry. The token then lives only here, so closing
// the preview sheet (X, backdrop, Esc, Back) can never bring it back: there is
// no '#p=' left in any history entry to re-read.
//
//   pendingSharedToken()        → token | null
//   clearSharedToken()          → forget it (the preview closed or the program was added)
//   useSharedLinkPending()      → token | null, re-renders on change
//   captureSharedHash(hash?)    → token | null (exported for tests)

import React from 'react';

let pending = null;
const subs = new Set();
const notify = () => subs.forEach(f => { try { f(pending); } catch (_) { /* ignore */ } });

// Same grammar as program_share.js tokenFromText (kept here so the shell does
// not have to load the share codec on first paint).
export function tokenFromHash(hash) {
  const m = String(hash || '').match(/[#&]p=([zj][A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

// The hash without its p= parameter ('' when nothing else was in it).
export function stripTokenFromHash(hash) {
  const h = String(hash || '').replace(/^#/, '');
  const rest = h.split('&').filter(kv => kv && !/^p=/.test(kv)).join('&');
  return rest ? `#${rest}` : '';
}

export function captureSharedHash(hash) {
  const fromWindow = hash === undefined;
  if (fromWindow && typeof window === 'undefined') return null;
  const h = fromWindow ? window.location.hash : hash;
  const t = tokenFromHash(h);
  if (!t) return null;
  if (fromWindow) {
    try {
      const { pathname, search } = window.location;
      window.history.replaceState(window.history.state, '', pathname + search + stripTokenFromHash(h));
    } catch (_) { /* ignore */ }
  }
  if (t !== pending) { pending = t; notify(); }
  return t;
}

export const pendingSharedToken = () => pending;
export function clearSharedToken() {
  if (pending == null) return;
  pending = null;
  notify();
}

export function useSharedLinkPending() {
  const [t, setT] = React.useState(pending);
  React.useEffect(() => { subs.add(setT); setT(pending); return () => { subs.delete(setT); }; }, []);
  return t;
}

if (typeof window !== 'undefined') {
  captureSharedHash();
  window.addEventListener('hashchange', () => captureSharedHash());
}
